import React, { useState, useEffect } from 'react';
import { Copy, Plus, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from '@cotera/client/app/components';
import { Ty } from '@cotera/era';
import { writeToClipboard } from '@cotera/client/app/components/ui/tiptap/tiptap-ui/copy-to-clipboard-button/use-copy-to-clipboard';
import { Chip } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@cotera/client/v0/components/ui/accordion';
import { AIResponse } from '@cotera/client/v0/components/ui/ai/response';
import type { ToolInfoCardProps, AuthStrategyInfo } from './types';
import {
  extractDescriptionSummary,
  generateToolMentionHtml,
  getAuthStrategyLabel,
} from './utils';

/**
 * Select button for choosing a tool.
 */
const SelectButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <button
    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <Plus className="h-3.5 w-3.5" />
    Select
  </button>
);

/**
 * Auth strategy pills display component.
 */
const AuthStrategyPills: React.FC<{
  strategies: AuthStrategyInfo[];
}> = ({ strategies }) => (
  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:shrink-0">
    {strategies.map(({ name, isActive, displayLabel }) => (
      <Chip
        key={name + (displayLabel ?? '')}
        variant={isActive ? 'success' : 'neutral'}
      >
        {displayLabel ?? getAuthStrategyLabel(name, 'short')}
      </Chip>
    ))}
  </div>
);

/**
 * Why this tool cannot run. Shown in place of nothing at all — previously the
 * reason reached the @-mention tooltip but never the catalog, which is the one
 * screen built for resolving it.
 */
const NotExecutableNote: React.FC<{ reason: string }> = ({ reason }) => (
  <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
    <span className="min-w-0">{reason}</span>
  </p>
);

/**
 * Schema display section for input and output types.
 */
const SchemaDisplay: React.FC<{
  inputTy: Ty.ExtendedAttributeType;
  outputTy: Ty.ExtendedAttributeType;
  inputFieldDescriptions?: Record<string, string>;
}> = ({ inputTy, outputTy, inputFieldDescriptions }) => (
  <div className="flex flex-col gap-4">
    {inputFieldDescriptions !== undefined &&
      Object.keys(inputFieldDescriptions).length > 0 && (
        <ParameterList descriptions={inputFieldDescriptions} />
      )}
    {/* One column until there is room for two: this card also renders in the
        catalog's right pane, where a forced 2-up put two monospace blocks in
        roughly half the width they need. */}
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Input Parameters */}
      <div className="bg-background rounded-xl border border-border/50 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border/50 bg-muted/30">
          <span className="text-xs font-semibold text-foreground/80">
            Input Parameters
          </span>
        </div>
        <pre className="p-4 text-xs overflow-auto max-h-60 font-mono leading-relaxed whitespace-pre">
          <code className="text-foreground/90">
            {Ty.prettyDisplayTy(inputTy)}
          </code>
        </pre>
      </div>

      {/* Output Type */}
      <div className="bg-background rounded-xl border border-border/50 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border/50 bg-muted/30">
          <span className="text-xs font-semibold text-foreground/80">
            Output Type
          </span>
        </div>
        <pre className="p-4 text-xs overflow-auto max-h-60 font-mono leading-relaxed whitespace-pre">
          <code className="text-foreground/90">
            {Ty.prettyDisplayTy(outputTy)}
          </code>
        </pre>
      </div>
    </div>
  </div>
);

/**
 * Plain-language notes for the input parameters, which the raw type cannot
 * carry — `prettyDisplayTy` prints names and types only.
 */
const ParameterList: React.FC<{ descriptions: Record<string, string> }> = ({
  descriptions,
}) => (
  <div className="bg-background rounded-xl border border-border/50 overflow-hidden">
    <div className="px-4 py-2.5 border-b border-border/50 bg-muted/30">
      <span className="text-xs font-semibold text-foreground/80">
        Parameters
      </span>
    </div>
    <dl className="px-4 py-3 flex flex-col gap-2">
      {Object.entries(descriptions).map(([field, text]) => (
        <div key={field} className="flex flex-col gap-0.5">
          <dt className="text-xs font-mono text-foreground/90">{field}</dt>
          <dd className="text-xs text-muted-foreground">{text}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/**
 * Tool name with copy mention functionality.
 */
const ToolNameWithCopy: React.FC<{
  name: string;
  toolKey: string;
  showCopyMention: boolean;
  onCopy?: () => void;
}> = ({ name, toolKey, showCopyMention, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const html = generateToolMentionHtml(toolKey, name);
    const plainText = `@${name}`;
    await writeToClipboard(plainText, html);
    setCopied(true);
    toast.success('Copied to clipboard');
    onCopy?.();
    setTimeout(() => setCopied(false), 2000);
  };

  if (!showCopyMention) {
    return (
      <span className="min-w-0 font-semibold text-foreground">{name}</span>
    );
  }

  return (
    // `min-w-0` and a `shrink-0` glyph: without them the button held its
    // content width and the name wrapped a word per line rather than giving,
    // and the copy icon was the first thing squeezed out of a narrow row.
    <button
      className="group inline-flex min-w-0 items-center gap-1.5 text-left transition-colors hover:text-primary"
      onClick={handleCopy}
    >
      <span className="min-w-0 font-semibold text-foreground group-hover:text-primary">
        {name}
      </span>
      {copied ? (
        <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-hover:text-primary/70" />
      )}
    </button>
  );
};

/**
 * Reusable tool information card component.
 * Supports two variants:
 * - 'accordion': Expandable card with schema details (for use inside a parent Accordion)
 * - 'card': Static card without expansion (for chat/inline use)
 *
 * Note: The 'accordion' variant renders an AccordionItem and expects to be placed
 * inside a parent Accordion component for proper state management.
 */
export const ToolInfoCard: React.FC<ToolInfoCardProps> = ({
  name,
  description,
  toolKey,
  inputTy,
  outputTy,
  inputFieldDescriptions,
  authStrategies,
  canExecute,
  notExecutableReason,
  variant = 'accordion',
  showSchemas,
  showCopyMention = true,
  defaultExpanded: _defaultExpanded,
  highlighted: initialHighlight = false,
  className,
  onCopy,
  onSelect,
}) => {
  const [showHighlight, setShowHighlight] = useState(initialHighlight);

  // Determine if schemas should be shown based on variant
  const shouldShowSchemas = showSchemas ?? variant === 'accordion';

  // Brief highlight that fades after a moment
  useEffect(() => {
    if (!initialHighlight) {
      return;
    }
    setShowHighlight(true);
    const timer = setTimeout(() => setShowHighlight(false), 2000);
    return () => clearTimeout(timer);
  }, [initialHighlight]);

  // Card variant - static display
  if (variant === 'card') {
    return (
      <div
        className={cn(
          'px-5 py-4 bg-background rounded-xl border border-border/60 transition-all',
          showHighlight && 'border-primary/30 bg-accent/30 shadow-sm',
          className
        )}
      >
        {/* Stacked on a phone. Side by side, the pills are `shrink-0` and the
            name column has no floor, so a card ~290px wide gave the name about
            90px — "Apollo Organization Enrichment" came out three lines tall
            beside two pills holding their full width, and the row's min-content
            still overflowed the card. Given its own line the name has the whole
            width, and the pills read as what they are: facts about the tool
            above them. */}
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1 text-left sm:w-auto">
            <div className="flex items-center gap-2">
              <ToolNameWithCopy
                name={name}
                toolKey={toolKey}
                showCopyMention={showCopyMention}
                onCopy={onCopy}
              />
            </div>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground sm:line-clamp-1">
              {extractDescriptionSummary(description)}
            </p>
            {!canExecute && notExecutableReason !== undefined && (
              <NotExecutableNote reason={notExecutableReason} />
            )}
          </div>
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:flex-nowrap sm:shrink-0">
            <AuthStrategyPills strategies={authStrategies} />
            {onSelect !== undefined && canExecute && (
              <SelectButton onClick={onSelect} />
            )}
          </div>
        </div>

        {shouldShowSchemas && (
          <div className="mt-4">
            <SchemaDisplay
              inputTy={inputTy}
              outputTy={outputTy}
              inputFieldDescriptions={inputFieldDescriptions}
            />
          </div>
        )}
      </div>
    );
  }

  // Accordion variant - renders AccordionItem (expects parent Accordion)
  return (
    <AccordionItem
      value={toolKey}
      className={cn(
        'border-none rounded-xl transition-all duration-500',
        showHighlight && 'bg-primary/5',
        className
      )}
    >
      <AccordionTrigger
        className={cn(
          'group hover:no-underline px-5 py-4 bg-background rounded-xl border border-border/60 hover:border-primary/30 hover:bg-accent/30 hover:shadow-sm transition-all data-[state=open]:rounded-b-none data-[state=open]:border-b-0',
          showHighlight && 'border-primary/30 bg-accent/30 shadow-sm'
        )}
      >
        {/* See the card variant above for why this stacks. */}
        <div className="flex w-full flex-col gap-2 pr-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1 text-left sm:w-auto">
            <div className="flex items-center gap-2">
              <ToolNameWithCopy
                name={name}
                toolKey={toolKey}
                showCopyMention={showCopyMention}
                onCopy={onCopy}
              />
            </div>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground group-data-[state=open]:hidden sm:line-clamp-1">
              {extractDescriptionSummary(description)}
            </p>
            {!canExecute && notExecutableReason !== undefined && (
              <NotExecutableNote reason={notExecutableReason} />
            )}
          </div>
          <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:flex-nowrap sm:shrink-0">
            <AuthStrategyPills strategies={authStrategies} />
            {onSelect !== undefined && canExecute && (
              <SelectButton onClick={onSelect} />
            )}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-5 pb-5 pt-0 bg-muted/20 border border-t-0 border-border/60 rounded-b-xl">
        <div className="pt-4 space-y-4">
          <AIResponse className="text-sm">{description}</AIResponse>
          <SchemaDisplay
            inputTy={inputTy}
            outputTy={outputTy}
            inputFieldDescriptions={inputFieldDescriptions}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
